import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, InputNumber, Radio, Slider, Spin, Tag, message } from 'antd'
import { ArrowLeftOutlined, ReloadOutlined, SaveOutlined, UndoOutlined, RedoOutlined } from '@ant-design/icons'
import {
  DEFAULT_FLAT_ENHANCE,
  FLAT_CROP_ASPECTS,
  FLAT_ENHANCE_SLIDERS,
  detectFlatBoundary,
  getFlatChain,
  previewFlatChain,
  saveFlatChain,
  saveFlatCrop,
  saveFlatTemplate,
  saveFlatBoundary,
  listFlatTemplates,
  type FlatBoundaryGeometry,
  type FlatBoundaryType,
  type FlatChainOut,
  type FlatCropParams,
  type FlatEnhanceParams,
  type FlatPoint,
  type FlatTemplateKey,
  type FlatTemplateOut,
} from '../../flatImageApi'
import { useWorkspaceState } from '../WorkspaceContext'
import './flat-image.css'

type Step = 1 | 2 | 3 | 4

const STEP_IDS: Step[] = [1, 2, 3, 4]
const STEP_KEYS = ['boundary', 'crop', 'enhance', 'preview'] as const

function fullBoundsQuad(w: number, h: number): FlatBoundaryGeometry {
  return {
    top_left: { x: 0, y: 0 },
    top_right: { x: w, y: 0 },
    bottom_right: { x: w, y: h },
    bottom_left: { x: 0, y: h },
  }
}

interface EditState {
  boundaryType: FlatBoundaryType
  geometry: FlatBoundaryGeometry
  crop: FlatCropParams
  enhance: FlatEnhanceParams
  templateKey: FlatTemplateKey
}

function stateFromChain(chain: FlatChainOut): EditState {
  const w = chain.original_width || 1000
  const h = chain.original_height || 1000
  return {
    boundaryType: (chain.flat_boundary_type as FlatBoundaryType) || 'auto',
    geometry: chain.flat_boundary_geometry && Object.keys(chain.flat_boundary_geometry).length
      ? chain.flat_boundary_geometry
      : fullBoundsQuad(w, h),
    crop: chain.flat_crop || { aspect: 'original', angle: 0, flip_h: false, flip_v: false },
    enhance: chain.flat_enhance || DEFAULT_FLAT_ENHANCE,
    templateKey: (chain.flat_template_key as FlatTemplateKey) || 'true_reproduction',
  }
}

export default function FlatImageEditorPage() {
  const { t } = useTranslation(['imageProcessing', 'navigation', 'actions'])
  const { workspace } = useWorkspaceState()
  const workspaceId = workspace?.id || ''
  const { chainId = '' } = useParams()
  const navigate = useNavigate()

  const imageAssetsLabel = t('navigation:tenantNav.images.assets')
  const flatOptimizationLabel = t('navigation:tenantNav.images.flatOptimization')
  const steps = useMemo(
    () => STEP_IDS.map((id) => ({ id, label: t(`editor.steps.${STEP_KEYS[id - 1]}`) })),
    [t],
  )
  const boundaryTypeLabel = (key: string) => t(`editor.boundaryTypes.${key}`, { defaultValue: key })

  const [chain, setChain] = useState<FlatChainOut | null>(null)
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState<Step>(1)
  const [edit, setEdit] = useState<EditState | null>(null)
  const [detecting, setDetecting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [templates, setTemplates] = useState<FlatTemplateOut[]>([])
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showBefore, setShowBefore] = useState(false)

  const historyRef = useRef<EditState[]>([])
  const historyIndexRef = useRef(-1)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const pushHistory = useCallback((next: EditState) => {
    const hist = historyRef.current.slice(0, historyIndexRef.current + 1)
    hist.push(next)
    historyRef.current = hist
    historyIndexRef.current = hist.length - 1
    setCanUndo(historyIndexRef.current > 0)
    setCanRedo(false)
  }, [])

  const commitEdit = useCallback(
    (updater: (prev: EditState) => EditState) => {
      setEdit((prev) => {
        if (!prev) return prev
        const next = updater(prev)
        pushHistory(next)
        return next
      })
    },
    [pushHistory],
  )

  const undo = () => {
    if (historyIndexRef.current <= 0) return
    historyIndexRef.current -= 1
    setEdit(historyRef.current[historyIndexRef.current])
    setCanUndo(historyIndexRef.current > 0)
    setCanRedo(true)
  }
  const redo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return
    historyIndexRef.current += 1
    setEdit(historyRef.current[historyIndexRef.current])
    setCanUndo(true)
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1)
  }
  const restoreOriginal = () => {
    if (!chain) return
    const w = chain.original_width || 1000
    const h = chain.original_height || 1000
    commitEdit(() => ({
      boundaryType: 'auto',
      geometry: fullBoundsQuad(w, h),
      crop: { aspect: 'original', angle: 0, flip_h: false, flip_v: false },
      enhance: DEFAULT_FLAT_ENHANCE,
      templateKey: 'true_reproduction',
    }))
  }
  const restoreStep = () => {
    if (!chain) return
    commitEdit((prev) => {
      const fromServer = stateFromChain(chain)
      if (step === 1) return { ...prev, boundaryType: fromServer.boundaryType, geometry: fromServer.geometry }
      if (step === 2) return { ...prev, crop: fromServer.crop }
      if (step === 3) return { ...prev, enhance: fromServer.enhance, templateKey: fromServer.templateKey }
      return prev
    })
  }

  const load = useCallback(async () => {
    if (!workspaceId || !chainId) return
    setLoading(true)
    try {
      const { data } = await getFlatChain(workspaceId, chainId)
      setChain(data)
      const initial = stateFromChain(data)
      setEdit(initial)
      historyRef.current = [initial]
      historyIndexRef.current = 0
      setCanUndo(false)
      setCanRedo(false)
    } catch (e) {
      message.error((e as Error).message || t('editor.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, chainId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!workspaceId) return
    listFlatTemplates(workspaceId)
      .then(({ data }) => setTemplates(data.templates))
      .catch(() => undefined)
  }, [workspaceId])

  // Fast, debounced, browser-visible-only preview (spec §二十六/§五十二 —
  // never writes R2, never enqueues a Job on every slider tick).
  const previewSeqRef = useRef(0)
  useEffect(() => {
    if (!workspaceId || !chainId || !edit || step === 1) return
    const seq = ++previewSeqRef.current
    setPreviewLoading(true)
    const timer = window.setTimeout(() => {
      previewFlatChain(workspaceId, chainId, {
        boundary_type: edit.boundaryType,
        geometry: edit.geometry,
        crop: edit.crop,
        enhance: edit.enhance,
      })
        .then(({ data }) => {
          if (seq !== previewSeqRef.current) return
          setPreviewUrl(data.preview_data_url)
        })
        .catch(() => undefined)
        .finally(() => {
          if (seq === previewSeqRef.current) setPreviewLoading(false)
        })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [workspaceId, chainId, edit, step])

  // -------------------------------------------------------- Step 1: boundary
  const stageRef = useRef<HTMLDivElement>(null)
  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const dragRef = useRef<{ kind: string; index?: number } | null>(null)

  const w = imgNaturalSize?.w || chain?.original_width || 1
  const h = imgNaturalSize?.h || chain?.original_height || 1

  const clientToImageCoords = (clientX: number, clientY: number): FlatPoint => {
    const el = stageRef.current
    if (!el) return { x: 0, y: 0 }
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(w, ((clientX - rect.left) / rect.width) * w))
    const y = Math.max(0, Math.min(h, ((clientY - rect.top) / rect.height) * h))
    return { x, y }
  }

  const onPointerMoveStage = (e: React.PointerEvent) => {
    if (!dragRef.current || !edit) return
    const pt = clientToImageCoords(e.clientX, e.clientY)
    const { kind, index } = dragRef.current
    setEdit((prev) => {
      if (!prev) return prev
      const g = { ...prev.geometry }
      if (kind === 'corner' && index !== undefined) {
        const keys = ['top_left', 'top_right', 'bottom_right', 'bottom_left'] as const
        g[keys[index]] = pt
      } else if (kind === 'circle_center') {
        g.cx = pt.x
        g.cy = pt.y
      } else if (kind === 'circle_radius') {
        g.r = Math.hypot(pt.x - (g.cx || 0), pt.y - (g.cy || 0))
      } else if (kind === 'ellipse_center') {
        g.cx = pt.x
        g.cy = pt.y
      } else if (kind === 'ellipse_rx') {
        g.rx = Math.abs(pt.x - (g.cx || 0))
      } else if (kind === 'ellipse_ry') {
        g.ry = Math.abs(pt.y - (g.cy || 0))
      } else if (kind === 'freeform_point' && index !== undefined) {
        const pts = [...(g.points || [])]
        pts[index] = pt
        g.points = pts
      }
      return { ...prev, geometry: g }
    })
  }
  const onPointerUpStage = () => {
    if (dragRef.current && edit) pushHistory(edit)
    dragRef.current = null
  }
  const startDrag = (kind: string, index?: number) => (e: React.PointerEvent) => {
    e.stopPropagation()
    dragRef.current = { kind, index }
  }
  const onStageClickForFreeform = (e: React.MouseEvent) => {
    if (!edit || edit.boundaryType !== 'freeform') return
    if (dragRef.current) return
    const pt = clientToImageCoords(e.clientX, e.clientY)
    commitEdit((prev) => ({
      ...prev,
      geometry: { ...prev.geometry, points: [...(prev.geometry.points || []), pt] },
    }))
  }

  const runDetect = async () => {
    if (!workspaceId || !chainId) return
    setDetecting(true)
    try {
      const { data } = await detectFlatBoundary(workspaceId, chainId)
      setChain(data)
      commitEdit((prev) => ({
        ...prev,
        boundaryType: (data.flat_boundary_type as FlatBoundaryType) || 'unknown',
        geometry:
          data.flat_boundary_geometry && Object.keys(data.flat_boundary_geometry).length
            ? data.flat_boundary_geometry
            : prev.geometry,
      }))
      if (data.current_message) message.warning(data.current_message)
      else message.success(t('editor.detectedBoundary', { type: boundaryTypeLabel(data.flat_boundary_type || '') }))
    } catch (e) {
      message.error((e as Error).message || t('editor.detectFailed'))
    } finally {
      setDetecting(false)
    }
  }

  const setBoundaryType = (t: FlatBoundaryType) => {
    commitEdit((prev) => {
      // Auto-detection (or a prior manual switch) may have left `geometry`
      // shaped for a *different* boundary type — e.g. an ellipse fit
      // (cx/cy/rx/ry) sitting around when the user now picks "圆形". Reusing
      // that shape here would silently downgrade the user's explicit circle
      // choice into a lopsided ellipse on save, so each branch checks for
      // its own required field, not just a generic `cx === undefined`.
      if (t === 'quad' && !prev.geometry.top_left) return { ...prev, boundaryType: t, geometry: fullBoundsQuad(w, h) }
      if (t === 'circle' && prev.geometry.r === undefined)
        return { ...prev, boundaryType: t, geometry: { cx: w / 2, cy: h / 2, r: Math.min(w, h) / 3 } }
      if (t === 'ellipse' && prev.geometry.rx === undefined)
        return { ...prev, boundaryType: t, geometry: { cx: w / 2, cy: h / 2, rx: w / 3, ry: h / 4, angle: 0 } }
      if (t === 'freeform' && !prev.geometry.points)
        return { ...prev, boundaryType: t, geometry: { points: [] } }
      return { ...prev, boundaryType: t }
    })
  }

  const goToStep2 = async () => {
    if (!workspaceId || !chainId || !edit) return
    try {
      await saveFlatBoundary(workspaceId, chainId, { boundary_type: edit.boundaryType, geometry: edit.geometry })
      setStep(2)
    } catch (e) {
      message.error((e as Error).message || t('editor.saveBoundaryFailed'))
    }
  }

  const goToStep3 = async () => {
    if (!workspaceId || !chainId || !edit) return
    try {
      await saveFlatCrop(workspaceId, chainId, edit.crop)
      setStep(3)
    } catch (e) {
      message.error((e as Error).message || t('editor.saveCropFailed'))
    }
  }

  const applyTemplate = (key: FlatTemplateKey, customId?: string) => {
    const tpl = templates.find((t) => t.key === key && (key !== 'custom' || t.id === customId))
    commitEdit((prev) => ({
      ...prev,
      templateKey: key,
      enhance: (tpl?.defaults || tpl?.enhance || DEFAULT_FLAT_ENHANCE) as FlatEnhanceParams,
      crop: key === 'custom' && tpl?.crop ? tpl.crop : prev.crop,
    }))
  }

  const goToStep4 = async () => {
    if (!workspaceId || !chainId || !edit) return
    try {
      await saveFlatTemplate(workspaceId, chainId, { template_key: edit.templateKey, enhance: edit.enhance })
      setStep(4)
    } catch (e) {
      message.error((e as Error).message || t('editor.saveEnhanceFailed'))
    }
  }

  const doSave = async () => {
    if (!workspaceId || !chainId || !edit) return
    setSaving(true)
    try {
      const { data } = await saveFlatChain(workspaceId, chainId, {
        boundary_type: edit.boundaryType,
        geometry: edit.geometry,
        crop: edit.crop,
        enhance: edit.enhance,
      })
      setChain(data)
      message.success(t('editor.savedGenerating'))
      navigate(`/workspace/${workspaceId}/images/flat-optimization`)
    } catch (e) {
      message.error((e as Error).message || t('editor.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const displayImageUrl = step === 1 ? chain?.original_url : previewUrl || chain?.original_url

  if (loading || !chain || !edit) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div className="fie-page">
      <div className="fi-crumb">
        <Link to={`/workspace/${workspaceId}/images`}>{imageAssetsLabel}</Link>
        <span className="sep">›</span>
        <Link to={`/workspace/${workspaceId}/images/flat-optimization`}>{flatOptimizationLabel}</Link>
        <span className="sep">›</span>
        <span className="current">{chain.original_filename}</span>
        <span className="sep">·</span>
        <Link to={`/workspace/${workspaceId}/images/flat-optimization`}>
          <ArrowLeftOutlined /> {t('editor.backToWorkbench')}
        </Link>
      </div>

      <div className="fie-stepper">
        {steps.map((s, i) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', flex: i === steps.length - 1 ? 0 : 1 }}>
            <div
              className={`fie-step${step === s.id ? ' is-active' : ''}${step > s.id ? ' is-done' : ''}`}
              onClick={() => setStep(s.id)}
              role="button"
            >
              <span className="fie-step-num">{s.id}</span>
              {s.label}
            </div>
            {i < steps.length - 1 && <div className="fie-step-sep" />}
          </div>
        ))}
      </div>

      <div className="fie-body">
        {/* -------------------------------------------------------- Left */}
        <div className="fie-left">
          <div className="fie-panel">
            <h3>{t('editor.fileInfo')}</h3>
            <div className="fie-thumb">
              {chain.thumbnail_url && <img src={chain.thumbnail_url} alt="" />}
            </div>
            <div className="fie-info-row">
              <span>{t('editor.filename')}</span>
              <span title={chain.original_filename}>{chain.original_filename}</span>
            </div>
            <div className="fie-info-row">
              <span>{t('editor.uploadedAt')}</span>
              <span>{chain.created_at ? new Date(chain.created_at).toLocaleString() : '—'}</span>
            </div>
            <div className="fie-info-row">
              <span>{t('editor.originalSize')}</span>
              <span>
                {chain.original_width} × {chain.original_height}
              </span>
            </div>
            <div className="fie-info-row">
              <span>{t('editor.fileSize')}</span>
              <span>{chain.original_size_bytes ? `${(chain.original_size_bytes / 1024 / 1024).toFixed(1)} MB` : '—'}</span>
            </div>
            <div className="fie-info-row">
              <span>{t('editor.processTemplate')}</span>
              <span>{t(`templates.${edit.templateKey}`, { defaultValue: edit.templateKey })}</span>
            </div>
          </div>

          <div className="fie-panel">
            <h3>{t('editor.autoDetectResult')}</h3>
            {chain.flat_boundary_confidence_label ? (
              <>
                <div className="fie-info-row">
                  <span>{t('editor.detected')}</span>
                  <span>{chain.flat_boundary_type_label}</span>
                </div>
                <div className="fie-info-row">
                  <span>{t('editor.confidence')}</span>
                  <span>{chain.flat_boundary_confidence_label}</span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('editor.notRunYet')}</div>
            )}
            <Button
              size="small"
              style={{ marginTop: 8 }}
              loading={detecting}
              onClick={runDetect}
              icon={<ReloadOutlined />}
              block
            >
              {chain.flat_boundary_confidence_label ? t('editor.redetect') : t('editor.autoDetect')}
            </Button>
          </div>
        </div>

        {/* ------------------------------------------------------ Center */}
        <div className="fie-center">
          {step === 1 ? (
            <div
              className="fie-stage"
              ref={stageRef}
              onPointerMove={onPointerMoveStage}
              onPointerUp={onPointerUpStage}
              onClick={onStageClickForFreeform}
            >
              {chain.original_url && (
                <img
                  className="fie-stage-img"
                  src={chain.original_url}
                  alt=""
                  onLoad={(e) => {
                    const img = e.currentTarget
                    setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
                  }}
                />
              )}
              <svg
                className={`fie-stage-overlay${edit.boundaryType !== 'auto' && edit.boundaryType !== 'unknown' ? ' is-interactive' : ''}`}
                viewBox={`0 0 ${w} ${h}`}
                preserveAspectRatio="none"
              >
                <BoundaryOverlay boundaryType={edit.boundaryType} geometry={edit.geometry} startDrag={startDrag} w={w} h={h} />
              </svg>
            </div>
          ) : (
            <div className="fie-stage">
              {previewLoading && <Spin style={{ position: 'absolute', zIndex: 2 }} />}
              <img
                className="fie-stage-img"
                src={(showBefore ? chain.original_url : displayImageUrl) || undefined}
                alt=""
              />
            </div>
          )}

          {step !== 1 && (
            <div className="fie-toolbar-row">
              <Radio.Group value={showBefore ? 'before' : 'after'} onChange={(e) => setShowBefore(e.target.value === 'before')}>
                <Radio.Button value="before">{t('editor.before')}</Radio.Button>
                <Radio.Button value="after">{t('editor.after')}</Radio.Button>
              </Radio.Group>
            </div>
          )}
          {step === 1 && edit.boundaryType === 'unknown' && (
            <div className="fie-hint">{t('editor.boundaryHint')}</div>
          )}
        </div>

        {/* ------------------------------------------------------- Right */}
        <div className="fie-right">
          <div className="fie-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{steps[step - 1].label}</h3>
              <div>
                <Button size="small" icon={<UndoOutlined />} disabled={!canUndo} onClick={undo} />
                <Button size="small" icon={<RedoOutlined />} disabled={!canRedo} onClick={redo} style={{ marginLeft: 4 }} />
              </div>
            </div>

            {step === 1 && (
              <Step1Panel edit={edit} setBoundaryType={setBoundaryType} commitEdit={commitEdit} w={w} h={h} boundaryTypeLabel={boundaryTypeLabel} />
            )}
            {step === 2 && <Step2Panel edit={edit} commitEdit={commitEdit} />}
            {step === 3 && (
              <Step3Panel edit={edit} commitEdit={commitEdit} templates={templates} applyTemplate={applyTemplate} />
            )}
            {step === 4 && <Step4Panel chain={chain} />}
          </div>

          <Button block onClick={restoreStep}>
            {t('editor.restoreStep')}
          </Button>
          <Button block onClick={restoreOriginal}>
            {t('editor.restoreOriginal')}
          </Button>
        </div>
      </div>

      <div className="fie-footer-actions">
        <div>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/images/flat-optimization`)}>{t('editor.cancel')}</Button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {step > 1 && <Button onClick={() => setStep((step - 1) as Step)}>{t('editor.previous')}</Button>}
          {step < 4 && (
            <Button
              type="primary"
              onClick={step === 1 ? goToStep2 : step === 2 ? goToStep3 : goToStep4}
            >
              {t('editor.next', { step: steps[step]?.label ?? '' })}
            </Button>
          )}
          {step === 4 && (
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={doSave}>
              {t('editor.saveOutput')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- Overlay

function BoundaryOverlay({
  boundaryType,
  geometry,
  startDrag,
  w,
  h,
}: {
  boundaryType: FlatBoundaryType
  geometry: FlatBoundaryGeometry
  startDrag: (kind: string, index?: number) => (e: React.PointerEvent) => void
  w: number
  h: number
}) {
  const handleR = Math.max(4, Math.min(w, h) * 0.012)
  if (boundaryType === 'quad' && geometry.top_left) {
    const fallback: FlatPoint = { x: 0, y: 0 }
    const pts: FlatPoint[] = [
      geometry.top_left ?? fallback,
      geometry.top_right ?? fallback,
      geometry.bottom_right ?? fallback,
      geometry.bottom_left ?? fallback,
    ]
    const path = pts.map((p) => `${p.x},${p.y}`).join(' ')
    return (
      <>
        <polygon className="fie-boundary-path" points={path} />
        {pts.map((p, i) => (
          <circle
            key={i}
            className="fie-handle"
            cx={p.x}
            cy={p.y}
            r={handleR}
            onPointerDown={startDrag('corner', i)}
          />
        ))}
      </>
    )
  }
  if (boundaryType === 'circle' && geometry.cx !== undefined) {
    return (
      <>
        <circle
          className="fie-boundary-path"
          cx={geometry.cx}
          cy={geometry.cy}
          r={geometry.r}
        />
        <circle className="fie-handle" cx={geometry.cx} cy={geometry.cy} r={handleR} onPointerDown={startDrag('circle_center')} />
        <circle
          className="fie-handle"
          cx={(geometry.cx || 0) + (geometry.r || 0)}
          cy={geometry.cy}
          r={handleR}
          onPointerDown={startDrag('circle_radius')}
        />
      </>
    )
  }
  if (boundaryType === 'ellipse' && geometry.cx !== undefined) {
    return (
      <>
        <ellipse
          className="fie-boundary-path"
          cx={geometry.cx}
          cy={geometry.cy}
          rx={geometry.rx}
          ry={geometry.ry}
          transform={geometry.angle ? `rotate(${geometry.angle} ${geometry.cx} ${geometry.cy})` : undefined}
        />
        <circle className="fie-handle" cx={geometry.cx} cy={geometry.cy} r={handleR} onPointerDown={startDrag('ellipse_center')} />
        <circle
          className="fie-handle"
          cx={(geometry.cx || 0) + (geometry.rx || 0)}
          cy={geometry.cy}
          r={handleR}
          onPointerDown={startDrag('ellipse_rx')}
        />
        <circle
          className="fie-handle"
          cx={geometry.cx}
          cy={(geometry.cy || 0) + (geometry.ry || 0)}
          r={handleR}
          onPointerDown={startDrag('ellipse_ry')}
        />
      </>
    )
  }
  if (boundaryType === 'freeform') {
    const pts = geometry.points || []
    const path = pts.map((p) => `${p.x},${p.y}`).join(' ')
    return (
      <>
        {pts.length > 2 && <polygon className="fie-boundary-path" points={path} />}
        {pts.map((p, i) => (
          <circle
            key={i}
            className="fie-handle"
            cx={p.x}
            cy={p.y}
            r={handleR}
            onPointerDown={startDrag('freeform_point', i)}
          />
        ))}
      </>
    )
  }
  return null
}

// ------------------------------------------------------------- Step panels

function Step1Panel({
  edit,
  setBoundaryType,
  commitEdit,
  w,
  h,
  boundaryTypeLabel,
}: {
  edit: EditState
  setBoundaryType: (t: FlatBoundaryType) => void
  commitEdit: (updater: (prev: EditState) => EditState) => void
  w: number
  h: number
  boundaryTypeLabel: (key: string) => string
}) {
  const { t } = useTranslation('imageProcessing')
  const g = edit.geometry
  const corners = t('editor.corners', { returnObjects: true }) as string[]
  return (
    <>
      <div className="fie-tool-group">
        <div className="fie-tool-group-title">{t('editor.boundaryType')}</div>
        <div className="fie-boundary-type-grid">
          {(['quad', 'freeform', 'circle', 'ellipse'] as FlatBoundaryType[]).map((bt) => (
            <Button key={bt} size="small" type={edit.boundaryType === bt ? 'primary' : 'default'} onClick={() => setBoundaryType(bt)}>
              {boundaryTypeLabel(bt)}
            </Button>
          ))}
        </div>
      </div>

      {edit.boundaryType === 'quad' && g.top_left && (
        <div className="fie-tool-group">
          <div className="fie-tool-group-title">{t('editor.quadFineTune')}</div>
          {(['top_left', 'top_right', 'bottom_right', 'bottom_left'] as const).map((k, i) => (
            <div className="fie-corner-input-row" key={k}>
              <span className="label">{corners[i]}</span>
              <InputNumber
                size="small"
                value={Math.round(g[k]!.x)}
                onChange={(v) =>
                  commitEdit((prev) => ({ ...prev, geometry: { ...prev.geometry, [k]: { ...prev.geometry[k]!, x: v || 0 } } }))
                }
                addonBefore="X"
                style={{ width: 100 }}
              />
              <InputNumber
                size="small"
                value={Math.round(g[k]!.y)}
                onChange={(v) =>
                  commitEdit((prev) => ({ ...prev, geometry: { ...prev.geometry, [k]: { ...prev.geometry[k]!, y: v || 0 } } }))
                }
                addonBefore="Y"
                style={{ width: 100 }}
              />
            </div>
          ))}
          <Button size="small" block onClick={() => commitEdit((prev) => ({ ...prev, geometry: fullBoundsQuad(w, h) }))}>
            {t('editor.resetPerspective')}
          </Button>
        </div>
      )}

      {edit.boundaryType === 'circle' && g.cx !== undefined && (
        <div className="fie-tool-group">
          <div className="fie-tool-group-title">{t('editor.circleFineTune')}</div>
          <div className="fie-corner-input-row">
            <span className="label">{t('editor.center')}</span>
            <InputNumber size="small" value={Math.round(g.cx)} addonBefore="X" style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, cx: v || 0 } }))} />
            <InputNumber size="small" value={Math.round(g.cy || 0)} addonBefore="Y" style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, cy: v || 0 } }))} />
          </div>
          <div className="fie-corner-input-row">
            <span className="label">{t('editor.radius')}</span>
            <InputNumber size="small" value={Math.round(g.r || 0)} style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, r: v || 0 } }))} />
          </div>
        </div>
      )}

      {edit.boundaryType === 'ellipse' && g.cx !== undefined && (
        <div className="fie-tool-group">
          <div className="fie-tool-group-title">{t('editor.ellipseFineTune')}</div>
          <div className="fie-corner-input-row">
            <span className="label">{t('editor.center')}</span>
            <InputNumber size="small" value={Math.round(g.cx)} addonBefore="X" style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, cx: v || 0 } }))} />
            <InputNumber size="small" value={Math.round(g.cy || 0)} addonBefore="Y" style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, cy: v || 0 } }))} />
          </div>
          <div className="fie-corner-input-row">
            <span className="label">{t('editor.axes')}</span>
            <InputNumber size="small" value={Math.round(g.rx || 0)} style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, rx: v || 0 } }))} />
            <InputNumber size="small" value={Math.round(g.ry || 0)} style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, ry: v || 0 } }))} />
          </div>
          <div className="fie-corner-input-row">
            <span className="label">{t('editor.rotation')}</span>
            <InputNumber size="small" value={Math.round(g.angle || 0)} style={{ width: 100 }} onChange={(v) => commitEdit((p) => ({ ...p, geometry: { ...p.geometry, angle: v || 0 } }))} />
          </div>
        </div>
      )}

      {edit.boundaryType === 'freeform' && (
        <div className="fie-tool-group">
          <div className="fie-tool-group-title">{t('editor.freeformTitle', { count: (g.points || []).length })}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{t('editor.freeformHint')}</div>
          <Button
            size="small"
            block
            onClick={() => commitEdit((p) => ({ ...p, geometry: { points: (p.geometry.points || []).slice(0, -1) } }))}
          >
            {t('editor.undoLastPoint')}
          </Button>
          <Button size="small" block style={{ marginTop: 6 }} onClick={() => commitEdit((p) => ({ ...p, geometry: { points: [] } }))}>
            {t('editor.clear')}
          </Button>
        </div>
      )}
    </>
  )
}

function Step2Panel({ edit, commitEdit }: { edit: EditState; commitEdit: (u: (p: EditState) => EditState) => void }) {
  const { t } = useTranslation('imageProcessing')
  return (
    <>
      <div className="fie-tool-group">
        <div className="fie-tool-group-title">{t('editor.cropAspect')}</div>
        <div className="fie-aspect-grid">
          {FLAT_CROP_ASPECTS.map((a) => (
            <Button
              key={a.value}
              size="small"
              type={edit.crop.aspect === a.value ? 'primary' : 'default'}
              onClick={() => commitEdit((p) => ({ ...p, crop: { ...p.crop, aspect: a.value } }))}
            >
              {t(`editor.cropAspects.${a.value}`, { defaultValue: a.label })}
            </Button>
          ))}
        </div>
      </div>
      <div className="fie-tool-group">
        <div className="fie-tool-group-title">{t('editor.rotateFlip')}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <Button size="small" onClick={() => commitEdit((p) => ({ ...p, crop: { ...p.crop, angle: Math.max(-45, p.crop.angle - 5) } }))}>
            {t('editor.rotateLeft')}
          </Button>
          <Button size="small" onClick={() => commitEdit((p) => ({ ...p, crop: { ...p.crop, angle: Math.min(45, p.crop.angle + 5) } }))}>
            {t('editor.rotateRight')}
          </Button>
        </div>
        <div className="fie-slider-row">
          <div className="fie-slider-label">
            <span>{t('editor.fineAngle')}</span>
            <span className="value">{edit.crop.angle.toFixed(0)}°</span>
          </div>
          <Slider
            min={-45}
            max={45}
            value={edit.crop.angle}
            onChange={(v) => commitEdit((p) => ({ ...p, crop: { ...p.crop, angle: v as number } }))}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            size="small"
            type={edit.crop.flip_h ? 'primary' : 'default'}
            onClick={() => commitEdit((p) => ({ ...p, crop: { ...p.crop, flip_h: !p.crop.flip_h } }))}
          >
            {t('editor.flipH')}
          </Button>
          <Button
            size="small"
            type={edit.crop.flip_v ? 'primary' : 'default'}
            onClick={() => commitEdit((p) => ({ ...p, crop: { ...p.crop, flip_v: !p.crop.flip_v } }))}
          >
            {t('editor.flipV')}
          </Button>
        </div>
      </div>
    </>
  )
}

function Step3Panel({
  edit,
  commitEdit,
  templates,
  applyTemplate,
}: {
  edit: EditState
  commitEdit: (u: (p: EditState) => EditState) => void
  templates: FlatTemplateOut[]
  applyTemplate: (key: FlatTemplateKey, customId?: string) => void
}) {
  const { t } = useTranslation('imageProcessing')
  const canonical = templates.filter((tpl) => !tpl.is_custom)
  return (
    <>
      <div className="fie-tool-group">
        <div className="fie-tool-group-title">{t('editor.processTemplate')}</div>
        <div className="fie-aspect-grid">
          {canonical.map((tpl) => (
            <Button
              key={tpl.key}
              size="small"
              type={edit.templateKey === tpl.key ? 'primary' : 'default'}
              onClick={() => applyTemplate(tpl.key as FlatTemplateKey)}
            >
              {tpl.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="fie-tool-group">
        <div className="fie-tool-group-title">{t('editor.adjustments')}</div>
        {FLAT_ENHANCE_SLIDERS.map((s) => (
          <div className="fie-slider-row" key={s.key}>
            <div className="fie-slider-label">
              <span>{t(`editor.enhanceSliders.${s.key}`, { defaultValue: s.label })}</span>
              <span className="value">{edit.enhance[s.key]}</span>
            </div>
            <Slider
              min={s.min}
              max={s.max}
              value={edit.enhance[s.key]}
              onChange={(v) =>
                commitEdit((p) => ({ ...p, templateKey: 'custom', enhance: { ...p.enhance, [s.key]: v as number } }))
              }
            />
          </div>
        ))}
      </div>
    </>
  )
}

function Step4Panel({ chain }: { chain: FlatChainOut }) {
  const { t } = useTranslation('imageProcessing')
  return (
    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
      <p>{t('editor.step4Hint1')}</p>
      <p>{t('editor.step4Hint2')}</p>
      {chain.flat_needs_review && <Tag color="warning">{t('editor.needsReviewTag')}</Tag>}
    </div>
  )
}
